import React, { useState, useEffect, useRef } from "react";

import { string } from "prop-types";
import { itemsShape, filtersShape } from "./prop-types.js";
import {
  ALL,
  ITEMS_TO_SHOW,
  SLIDE_SPEED,
  ACTIVE,
  PREV,
  NEXT,
} from "./constants.js";
import useMatchMedia from "../../common/useMatchMedia.js";

import SliderCard from "./SliderCard/SliderCard.jsx";
import BackOfCard from "./BackOfCard/BackOfCard.jsx";
import SliderNavigation from "./CardSliderNavigation/CardSliderNavigation.jsx";
import SliderFilter from "./CardSliderFilters/CardSliderFilters.jsx";
import Icon from "../Icon/Icon.jsx";

import Slider from "react-slick";

import cx from "classnames";

const propTypes = {
  title: string,
  items: itemsShape,
  filters: filtersShape,
};

const CardSliderApp = (props) => {
  const { items, filters, title } = props;

  const parentRef = useRef();
  const sliderRef = useRef();
  const backSliderRef = useRef();

  const [filteredItems, setFilteredItems] = useState(items || []);
  const [activeFilter, setActiveFilter] = useState(ALL);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  // matches _breakpoints.scss
  const isDesktop = useMatchMedia("(min-width: 681px)");

  useEffect(() => {
    if (activeFilter === ALL) {
      setFilteredItems(items);
    } else {
      setFilteredItems(
        items.filter((item) => item.sectors.includes(activeFilter)),
      );
    }
  }, [items, activeFilter]);

  // Reset slider to first slide after filter change
  useEffect(() => {
    setTimeout(() => {
      setActiveIndex(0);
      sliderRef?.current.slickGoTo(0);
    }, SLIDE_SPEED + 50);
  }, [filteredItems]);

  useEffect(() => {
    if (isFlipped) {
      const handleKeydown = (e) => {
        if (e.key === "Esc" || e.key === "Escape") {
          handleFlipCard(false);
        }
      };

      document.addEventListener("keydown", handleKeydown);

      return () => {
        document.removeEventListener("keydown", handleKeydown);
      };
    }
  }, [isFlipped]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter.text);
  };

  const handleClick = (direction) => {
    if (direction === PREV) {
      sliderRef.current.slickPrev();
    } else {
      sliderRef.current.slickNext();
    }
  };

  const handleFlipCard = (isOpen = true) => {
    setIsFlipped(isOpen);

    parentRef.current.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });

    if (!isDesktop) {
      // On mobile, back of card will take over screen and prevent user from scrolling
      document.body.classList.toggle("card-slider-open", isOpen);
    }
  };

  const handleClickPrev = () => {
    if (isFlipped) {
      backSliderRef.current.slickPrev();
      setTimeout(() => sliderRef.current.slickPrev(), SLIDE_SPEED);
    } else {
      sliderRef.current.slickPrev();
    }
  };

  const handleClickNext = () => {
    if (isFlipped) {
      backSliderRef.current.slickNext();
      setTimeout(() => sliderRef.current.slickNext(), SLIDE_SPEED);
    } else {
      sliderRef.current.slickNext();
    }
  };

  const getCardPosition = (idx) => {
    if (filteredItems.length === 1) return ACTIVE;

    const prevItemIsLast =
      activeIndex === 0 && idx === filteredItems.length - 1;
    const nextItemIsFirst =
      activeIndex === filteredItems.length - 1 && idx === 0;

    if (prevItemIsLast || idx === activeIndex - 1) {
      return PREV;
    } else if (nextItemIsFirst || idx === activeIndex + 1) {
      return NEXT;
    } else if (idx === activeIndex) {
      return ACTIVE;
    } else {
      return "";
    }
  };

  const settings = {
    arrows: false,
    centerMode: filteredItems.length > ITEMS_TO_SHOW,
    centerPadding: "60px",
    infinite: filteredItems.length > ITEMS_TO_SHOW,
    initialSlide: 0,
    slidesToShow: ITEMS_TO_SHOW,
    speed: SLIDE_SPEED,
    swipeToSlide: true,
    variableWidth: true,
    focusOnSelect: false,
    beforeChange: (current, next) => {
      if (filteredItems.length > ITEMS_TO_SHOW) {
        setActiveIndex(next);
      }
    },
    afterChange: (current) => {
      if (current !== 0) {
        setIsInitialRender(false);
      }
      if (!isFlipped && !isInitialRender) {
        parentRef.current
          .querySelector(".slick-current .slider-card.active")
          .focus();
      }
    },
  };

  return (
    <div
      ref={parentRef}
      className={cx("card-slider", { "card-slider--flipped": isFlipped })}
    >
      {title && <h2 className="card-slider__title">{title}</h2>}
      <SliderFilter
        filters={filters}
        activeFilter={activeFilter}
        onChange={handleFilterChange}
      />
      <div className="card-slider__cards container">
        {filteredItems && filteredItems.length > 0 && (
          <Slider ref={sliderRef} className="card-slider__front" {...settings}>
            {filteredItems.map((item, idx) => {
              const position = getCardPosition(idx);
              const isActive = idx === activeIndex;

              return (
                <SliderCard
                  key={idx}
                  index={idx}
                  item={item}
                  position={position}
                  activeFilter={activeFilter}
                  isActive={isActive}
                  isFlipped={isActive && isFlipped}
                  onClickCard={handleClick}
                  onFlipCard={handleFlipCard}
                />
              );
            })}
          </Slider>
        )}
        <div className={cx("slider-card__back", { flipped: isFlipped })}>
          <button
            type="button"
            className="slider-card__close"
            onClick={() => handleFlipCard(false)}
            aria-label="Close card"
          >
            <Icon iconName="close" />
          </button>
          {filteredItems && filteredItems.length > 0 && isFlipped && (
            <Slider
              ref={backSliderRef}
              accessibility={false}
              arrows={false}
              draggable={false}
              initialSlide={activeIndex}
              infinite={true}
              variableWidth={false}
              slidesToShow={1}
              speed={500}
              swipeToSlide={false}
            >
              {filteredItems.map((item, idx) => (
                <BackOfCard key={idx} {...item} />
              ))}
            </Slider>
          )}
        </div>
      </div>
      <SliderNavigation
        currentPage={activeIndex}
        totalPages={filteredItems.length}
        onClickPrev={handleClickPrev}
        onClickNext={handleClickNext}
      />
    </div>
  );
};

CardSliderApp.propTypes = propTypes;

export default CardSliderApp;
